import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'

interface TiltVector {
  x: number
  y: number
}

interface UseHeroTiltOptions {
  /** Max rotateX / rotateY in degrees */
  maxDeg?: number
  /** Pointer parallax translate in px at full tilt */
  parallaxPx?: number
  /** Optional external container ref (e.g. hero section wrapper) */
  containerRef?: RefObject<HTMLDivElement | null>
}

interface UseHeroTiltResult {
  containerRef: RefObject<HTMLDivElement | null>
  stageStyle: CSSProperties | undefined
  imageStyle: CSSProperties | undefined
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void
  onPointerLeave: () => void
  reducedMotion: boolean
}

function readReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Subtle mouse parallax for marketing hero — single base image only.
 * Disabled when prefers-reduced-motion is set.
 */
export function useHeroTilt(options: UseHeroTiltOptions = {}): UseHeroTiltResult {
  const { maxDeg = 4, parallaxPx = 14, containerRef: containerRefProp } = options
  const internalRef = useRef<HTMLDivElement>(null)
  const containerRef = containerRefProp ?? internalRef
  const [tilt, setTilt] = useState<TiltVector>({ x: 0, y: 0 })
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    setReducedMotion(readReducedMotion())
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (reducedMotion || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2
      setTilt({
        x: Math.max(-1, Math.min(1, x)),
        y: Math.max(-1, Math.min(1, y)),
      })
    },
    [reducedMotion]
  )

  const onPointerLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 })
  }, [])

  if (reducedMotion) {
    return {
      containerRef,
      stageStyle: undefined,
      imageStyle: undefined,
      onPointerMove,
      onPointerLeave,
      reducedMotion: true,
    }
  }

  const stageStyle: CSSProperties = {
    transform: `rotateX(${-tilt.y * maxDeg}deg) rotateY(${tilt.x * maxDeg}deg)`,
    transformStyle: 'preserve-3d',
    transition: 'transform 200ms ease-out',
    willChange: 'transform',
  }

  const imageStyle: CSSProperties = {
    transform: `translate3d(${tilt.x * parallaxPx}px, ${tilt.y * parallaxPx * 0.6}px, 0)`,
    transition: 'transform 200ms ease-out',
    willChange: 'transform',
  }

  return {
    containerRef,
    stageStyle,
    imageStyle,
    onPointerMove,
    onPointerLeave,
    reducedMotion: false,
  }
}
